import { cn } from "@repo/design-system/utils";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo } from "react";
import { SellerCartAccordion } from "../SellerCartAccordion";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface SellerCardListProps {
  /** The seller carts to render, one open card each. */
  carts: StoreSellerCart[];
  /** Carts the buyer has expanded. Every other cart is collapsed. */
  expandedCartIds: Set<string>;
  /** Toggle a cart's open/collapsed state. */
  onToggleCard: (cartId: string) => void;
  /** Map of cart ID to its applied promo code (card footer, KAN-75). */
  appliedPromoCodes: Record<string, string>;
  /** Map of cart ID to its promo error. */
  promoErrors: Record<string, string>;
  /** Apply a promo code to a cart. */
  onApplyPromo: (cartId: string, code: string) => Promise<void>;
  /** Remove a cart's applied promo code. */
  onRemovePromo: (cartId: string) => Promise<void>;
  className?: string;
}

/**
 * The Cart view's left column: one open seller card per supplier. Extracted
 * from the old CartTabs when the cart/compare tab switch became a toolbar
 * toggle (KAN-72) - the cards no longer share a component with the comparison
 * matrix, which now spans the full page width.
 */
export const SellerCardList = memo(function SellerCardList({
  carts,
  expandedCartIds,
  onToggleCard,
  appliedPromoCodes,
  promoErrors,
  onApplyPromo,
  onRemovePromo,
  className
}: SellerCardListProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {carts.map((cart) => (
        <SellerCartAccordion
          key={cart.cart_id}
          cart={cart}
          isExpanded={expandedCartIds.has(cart.cart_id)}
          onToggle={() => onToggleCard(cart.cart_id)}
          variant="by-supplier"
          appliedPromoCode={appliedPromoCodes[cart.cart_id]}
          promoError={promoErrors[cart.cart_id]}
          onApplyPromo={onApplyPromo}
          onRemovePromo={onRemovePromo}
        />
      ))}
    </div>
  );
});
